import React, { useState, useEffect } from "react";
import {
  Card, CardBody, CardHeader, Col, Container, Row, Button, Input, Table,
  Badge, Spinner, FormGroup, Label, Alert,
} from "reactstrap";
import { toast } from "react-toastify";
import { getReporteEntregasPlantas, getResumenEntregasPlantas, getPlantas } from "../../helpers/backend_helper";
import Flatpickr from "react-flatpickr";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const ReportePlantas = () => {
  const [loading, setLoading] = useState(false);
  const [loadingResumen, setLoadingResumen] = useState(true);
  const [resumen, setResumen] = useState(null);
  const [solicitudes, setSolicitudes] = useState([]);
  const [estadisticas, setEstadisticas] = useState(null);
  const [plantas, setPlantas] = useState([]);
  const [plantasFiltradas, setPlantasFiltradas] = useState([]);

  // Filtros
  const [filtros, setFiltros] = useState({
    planta_id: "",
    estado: "",
  });
  const [rangoFechas, setRangoFechas] = useState([]);

  const estadosVenezolanos = [
    "Amazonas", "Anzoátegui", "Apure", "Aragua", "Barinas", "Bolívar", "Carabobo",
    "Cojedes", "Delta Amacuro", "Distrito Capital","Falcón", "Guárico", "Lara", "La Guaira", "Mérida", "Miranda",
    "Monagas", "Nueva Esparta", "Portuguesa", "Sucre", "Táchira", "Trujillo",
    "Vargas", "Yaracuy", "Zulia"
  ];


  // Cargar resumen y plantas al montar
  useEffect(() => {
    cargarResumen();
    cargarPlantas();
  }, []);

  // Filtrar plantas cuando cambia el estado seleccionado
  useEffect(() => {
    if (filtros.estado) {
      const plantasDelEstado = plantas.filter(p => p.estado === filtros.estado);
      setPlantasFiltradas(plantasDelEstado);
      // Limpiar planta seleccionada si no está en el nuevo estado
      if (filtros.planta_id) {
        const plantaExiste = plantasDelEstado.some(p => p.id === filtros.planta_id);
        if (!plantaExiste) {
          setFiltros(prev => ({ ...prev, planta_id: "" }));
        }
      }
    } else {
      setPlantasFiltradas([]);
      setFiltros(prev => ({ ...prev, planta_id: "" }));
    }
  }, [filtros.estado, plantas]);

  const cargarResumen = async () => {
    setLoadingResumen(true);
    try {
      const response = await getResumenEntregasPlantas();
      if (response?.data?.data) {
        setResumen(response.data.data);
      }
    } catch (error) {
      console.error("Error cargando resumen:", error);
    } finally {
      setLoadingResumen(false);
    }
  };

  const cargarPlantas = async () => {
    try {
      const response = await getPlantas({ solo_activas: true });
      if (response?.data?.data) {
        setPlantas(response.data.data);
      }
    } catch (error) {
      console.error("Error cargando plantas:", error);
    }
  };

  const cargarReporte = async () => {
    // Validar que todos los campos obligatorios estén completos
    if (!filtros.estado) {
      toast.warning("⚠️ Debes seleccionar un Estado");
      return;
    }
    if (!filtros.planta_id) {
      toast.warning("⚠️ Debes seleccionar una Planta");
      return;
    }
    if (rangoFechas.length === 0) {
      toast.warning("⚠️ Debes seleccionar un Rango de Fechas");
      return;
    }

    setLoading(true);
    try {
      const params = {};
      if (filtros.planta_id) params.planta_id = filtros.planta_id;
      if (filtros.estado) params.estado = filtros.estado;
      
      // Procesar rango de fechas
      if (rangoFechas.length > 0) {
        params.fecha_desde = rangoFechas[0];
        if (rangoFechas.length > 1) {
          params.fecha_hasta = rangoFechas[1];
        } else {
          params.fecha_hasta = rangoFechas[0];
        }
      }

      const response = await getReporteEntregasPlantas(params);
      if (response?.data?.data) {
        setSolicitudes(response.data.data);
        setEstadisticas(response.data.estadisticas);
        toast.success(`✓ ${response.data.data.length} solicitudes encontradas`);
      }
    } catch (error) {
      console.error("Error cargando reporte:", error);
      toast.error("Error al cargar reporte");
    } finally {
      setLoading(false);
    }
  };

  const limpiarFiltros = () => {
    setFiltros({
      planta_id: "", estado: "",
    });
    setRangoFechas([]);
    setSolicitudes([]);
    setEstadisticas(null);
  };

  const exportarPDF = () => {
    try {
      if (!solicitudes || solicitudes.length === 0) {
        toast.warning("⚠️ No hay datos para exportar");
        return;
      }

      // Crear documento en orientación horizontal para más columnas
      const doc = new jsPDF('l', 'mm', 'a4');
      
      // Agregar logo de Farme
      try {
        const logoFarme = require('../../assets/images/logo-farme.png');
        doc.addImage(logoFarme, 'PNG', 14, 10, 40, 20);
      } catch (error) {
        console.warn("No se pudo cargar el logo:", error);
      }
      
      // Título del reporte con nombre de la planta
      const nombrePlanta = solicitudes[0]?.planta_detalle?.nombre || "Planta";
      const nombreFarmacia = solicitudes[0]?.entrega_planta?.asignado_por || "Farmacia";
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text(`Reporte de Entregas - ${nombrePlanta}`, 148, 18, { align: 'center' });
      
      // Período debajo del título
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      if (rangoFechas.length > 0) {
        // Parsear fechas sin conversión a Date para evitar problemas de zona horaria
        const formatearFecha = (fechaStr) => {
          const [year, month, day] = fechaStr.split('-');
          return `${day}/${month}/${year}`;
        };
        const fechaDesde = formatearFecha(rangoFechas[0]);
        const fechaHasta = rangoFechas.length > 1 ? formatearFecha(rangoFechas[1]) : fechaDesde;
        doc.text(`Período: ${fechaDesde} - ${fechaHasta}`, 148, 25, { align: 'center' });
      } else {
        doc.text('Período: Todos los registros', 148, 25, { align: 'center' });
      }
      
      // Farmacia debajo del período
      doc.text(`Farmacia: ${nombreFarmacia}`, 148, 30, { align: 'center' });
      
      // Preparar datos de la tabla con las nuevas columnas
      const tableData = solicitudes.map(s => {
        // Obtener el último delivery
        const ultimoDelivery = s.delivery && s.delivery.length > 0 ? s.delivery[s.delivery.length - 1] : null;
        const nombreDelivery = ultimoDelivery?.servicioEntrega 
          ? `${ultimoDelivery.servicioEntrega.nombre || ""} ${ultimoDelivery.servicioEntrega.apellido || ""}`.trim()
          : "-";
        const telefonoDelivery = ultimoDelivery?.servicioEntrega?.telefono || "N/A";
        
        return [
          s.numero_solicitud || "-",                                                    // Solicitud
          `${s.titular.nombre_completo || "-"}\nCI: ${s.titular.cedula || "N/A"}`,    // Titular
          `${s.solicitante?.nombre_completo || "-"}\nCI: ${s.solicitante?.cedula || "N/A"}`, // Solicitante
          ultimoDelivery?.servicioEntrega ? `${nombreDelivery}\n${telefonoDelivery}` : "Sin delivery", // Delivery
          ultimoDelivery?.fecha ? new Date(ultimoDelivery.fecha).toLocaleDateString('es-VE') : "-" // Fecha
        ];
      });
      
      // Crear tabla con autoTable
      autoTable(doc, {
        startY: 37,
        head: [[
          'Solicitud',
          'Titular',
          'Solicitante',
          'Delivery',
          'Fecha'
        ]],
        body: tableData,
        styles: { 
          fontSize: 9,
          cellPadding: 4,
          overflow: 'linebreak',
          halign: 'left',
          valign: 'middle'
        },
        headStyles: { 
          fillColor: [54, 96, 146],
          textColor: 255,
          fontStyle: 'bold',
          halign: 'center'
        },
        alternateRowStyles: {
          fillColor: [242, 242, 242]
        },
        columnStyles: {
          0: { cellWidth: 30, halign: 'center' },  // Solicitud
          1: { cellWidth: 65 },                     // Titular
          2: { cellWidth: 65 },                     // Solicitante
          3: { cellWidth: 60 },                     // Delivery
          4: { cellWidth: 30, halign: 'center' }   // Fecha
        },
        margin: { top: 10, left: 14, right: 14 },
        didDrawPage: (data) => {
          // Pie de página
          const pageCount = doc.internal.getNumberOfPages();
          const pageSize = doc.internal.pageSize;
          const pageHeight = pageSize.height ? pageSize.height : pageSize.getHeight();
          
          doc.setFontSize(8);
          doc.setFont('helvetica', 'italic');
          doc.text(
            `Reporte generado el: ${new Date().toLocaleString('es-VE')}`,
            14,
            pageHeight - 10
          );
          doc.text(
            `Página ${data.pageNumber} de ${pageCount}`,
            pageSize.width - 14,
            pageHeight - 10,
            { align: 'right' }
          );
        }
      });
      
      // Guardar
      doc.save(`reporte-entregas-plantas-${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success("✓ PDF generado correctamente");
    } catch (error) {
      console.error("Error al generar PDF:", error);
      toast.error("❌ Error al generar el PDF: " + error.message);
    }
  };

  const getBadgeColor = (estatus) => {
    const colors = {
      "Nueva Solicitud": "primary",
      "En Transito": "info",
      "Por Reprogramar": "warning",
      "Entregado": "success",
      "Cancelado": "secondary",
      "Rechazado": "danger",
    };
    return colors[estatus] || "secondary";
  };

  document.title = "Reporte de Plantas | Farme";

  return (
    <div className="page-content">
      <Container fluid>
        {/* Resumen General */}
        <Row className="mb-3">
          {/* Plantas Activas */}
          <Col xl={3} md={6}>
            <div 
              className="card card-animate overflow-hidden" 
              style={{ borderLeft: '4px solid var(--vz-primary)' }}
            >
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1 overflow-hidden">
                    {loadingResumen ? (
                      <div className="placeholder-glow">
                        <p className="text-uppercase fw-medium text-muted text-truncate mb-3">
                          <span className="placeholder col-8"></span>
                        </p>
                        <h4 className="fs-22 fw-semibold ff-secondary mb-0">
                          <span className="placeholder col-4"></span>
                        </h4>
                      </div>
                    ) : (
                      <>
                        <p className="text-uppercase fw-medium text-muted text-truncate mb-3">
                          Plantas Activas
                        </p>
                        <h4 className="fs-22 fw-semibold ff-secondary mb-0">
                          {resumen?.total_plantas_activas || 0}
                        </h4>
                      </>
                    )}
                  </div>
                  <div className="flex-shrink-0">
                    {loadingResumen ? (
                      <div className="avatar-sm placeholder-glow">
                        <span className="placeholder rounded" style={{ width: '100%', height: '100%' }}></span>
                      </div>
                    ) : (
                      <div className="avatar-sm">
                        <span className="avatar-title bg-primary-subtle text-primary rounded fs-3">
                          <i className="ri-building-line"></i>
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="mt-3">
                  {loadingResumen ? (
                    <span className="placeholder-glow"><span className="placeholder col-5"></span></span>
                  ) : (
                    <span className="badge bg-primary-subtle text-primary">
                      <i className="ri-building-line me-1"></i>Registradas
                    </span>
                  )}
                </div>
              </div>
            </div>
          </Col>

          {/* Solicitudes En Tránsito */}
          <Col xl={3} md={6}>
            <div 
              className="card card-animate overflow-hidden" 
              style={{ borderLeft: '4px solid var(--vz-success)' }}
            >
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1 overflow-hidden">
                    {loadingResumen ? (
                      <div className="placeholder-glow">
                        <p className="text-uppercase fw-medium text-muted text-truncate mb-3">
                          <span className="placeholder col-8"></span>
                        </p>
                        <h4 className="fs-22 fw-semibold ff-secondary mb-0">
                          <span className="placeholder col-4"></span>
                        </h4>
                      </div>
                    ) : (
                      <>
                        <p className="text-uppercase fw-medium text-muted text-truncate mb-3">
                          Solicitudes En Tránsito
                        </p>
                        <h4 className="fs-22 fw-semibold ff-secondary mb-0">
                          {resumen?.total_solicitudes_con_planta || 0}
                        </h4>
                      </>
                    )}
                  </div>
                  <div className="flex-shrink-0">
                    {loadingResumen ? (
                      <div className="avatar-sm placeholder-glow">
                        <span className="placeholder rounded" style={{ width: '100%', height: '100%' }}></span>
                      </div>
                    ) : (
                      <div className="avatar-sm">
                        <span className="avatar-title bg-success-subtle text-success rounded fs-3">
                          <i className="ri-file-list-line"></i>
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="mt-3">
                  {loadingResumen ? (
                    <span className="placeholder-glow"><span className="placeholder col-5"></span></span>
                  ) : (
                    <span className="badge bg-success-subtle text-success">
                      <i className="ri-truck-line me-1"></i>Con planta asignada
                    </span>
                  )}
                </div>
              </div>
            </div>
          </Col>

          {/* Estados con Entregas */}
          <Col xl={3} md={6}>
            <div 
              className="card card-animate overflow-hidden" 
              style={{ borderLeft: '4px solid var(--vz-info)' }}
            >
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1 overflow-hidden">
                    {loadingResumen ? (
                      <div className="placeholder-glow">
                        <p className="text-uppercase fw-medium text-muted text-truncate mb-3">
                          <span className="placeholder col-6"></span>
                        </p>
                        <h4 className="fs-22 fw-semibold ff-secondary mb-0">
                          <span className="placeholder col-4"></span>
                        </h4>
                      </div>
                    ) : (
                      <>
                        <p className="text-uppercase fw-medium text-muted text-truncate mb-3">
                          Estados con Entregas
                        </p>
                        <h4 className="fs-22 fw-semibold ff-secondary mb-0">
                          {Object.keys(resumen?.estados_con_entregas || {}).length}
                        </h4>
                      </>
                    )}
                  </div>
                  <div className="flex-shrink-0">
                    {loadingResumen ? (
                      <div className="avatar-sm placeholder-glow">
                        <span className="placeholder rounded" style={{ width: '100%', height: '100%' }}></span>
                      </div>
                    ) : (
                      <div className="avatar-sm">
                        <span className="avatar-title bg-info-subtle text-info rounded fs-3">
                          <i className="ri-map-pin-line"></i>
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="mt-3">
                  {loadingResumen ? (
                    <span className="placeholder-glow"><span className="placeholder col-5"></span></span>
                  ) : (
                    <span className="badge bg-info-subtle text-info">
                      <i className="ri-map-pin-line me-1"></i>Cobertura
                    </span>
                  )}
                </div>
              </div>
            </div>
          </Col>

          {/* Plantas con Entregas */}
          <Col xl={3} md={6}>
            <div 
              className="card card-animate overflow-hidden" 
              style={{ borderLeft: '4px solid var(--vz-warning)' }}
            >
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1 overflow-hidden">
                    {loadingResumen ? (
                      <div className="placeholder-glow">
                        <p className="text-uppercase fw-medium text-muted text-truncate mb-3">
                          <span className="placeholder col-6"></span>
                        </p>
                        <h4 className="fs-22 fw-semibold ff-secondary mb-0">
                          <span className="placeholder col-4"></span>
                        </h4>
                      </div>
                    ) : (
                      <>
                        <p className="text-uppercase fw-medium text-muted text-truncate mb-3">
                          Plantas con Entregas
                        </p>
                        <h4 className="fs-22 fw-semibold ff-secondary mb-0">
                          {Object.keys(resumen?.plantas_con_entregas || {}).length}
                        </h4>
                      </>
                    )}
                  </div>
                  <div className="flex-shrink-0">
                    {loadingResumen ? (
                      <div className="avatar-sm placeholder-glow">
                        <span className="placeholder rounded" style={{ width: '100%', height: '100%' }}></span>
                      </div>
                    ) : (
                      <div className="avatar-sm">
                        <span className="avatar-title bg-warning-subtle text-warning rounded fs-3">
                          <i className="ri-truck-line"></i>
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="mt-3">
                  {loadingResumen ? (
                    <span className="placeholder-glow"><span className="placeholder col-5"></span></span>
                  ) : (
                    <span className="badge bg-warning-subtle text-warning">
                      <i className="ri-building-line me-1"></i>Activas
                    </span>
                  )}
                </div>
              </div>
            </div>
          </Col>
        </Row>

        {/* Filtros y Reporte */}
        <Row>
          <Col lg={12}>
            <Card>
              <CardHeader className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                  <i className="ri-file-chart-line me-2 text-primary fs-20"></i>
                  <h5 className="card-title mb-0">Reporte Detallado de Entregas en Plantas</h5>
                </div>
                {solicitudes.length > 0 && (
                  <Button color="success" onClick={exportarPDF}>
                    <i className="ri-file-pdf-line me-1"></i>Exportar PDF
                  </Button>
                )}
              </CardHeader>
              <CardBody>
                {/* Filtros */}
                <Alert color="warning" className="mb-3">
                  <i className="ri-alert-line me-2"></i>
                  <strong>Importante:</strong> Todos los filtros son <strong>obligatorios</strong>. Selecciona Estado, Planta y Rango de Fechas para generar el reporte.
                </Alert>
                <Row className="mb-4">
                  <Col md={4}>
                    <FormGroup>
                      <Label>Estado <span className="text-danger">*</span></Label>
                      <Input type="select" value={filtros.estado}
                        onChange={(e) => setFiltros({ ...filtros, estado: e.target.value })}>
                        <option value="">Selecciona un estado</option>
                        {estadosVenezolanos.map(e => <option key={e} value={e}>{e}</option>)}
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md={4}>
                    <FormGroup>
                      <Label>Planta <span className="text-danger">*</span></Label>
                      <Input 
                        type="select" 
                        value={filtros.planta_id}
                        onChange={(e) => setFiltros({ ...filtros, planta_id: e.target.value })}
                        disabled={!filtros.estado}
                      >
                        <option value="">Selecciona una planta</option>
                        {Array.isArray(plantasFiltradas) && plantasFiltradas.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                      </Input>
                      {!filtros.estado && (
                        <small className="text-muted">Selecciona un estado primero</small>
                      )}
                    </FormGroup>
                  </Col>
                  <Col md={4}>
                    <FormGroup>
                      <Label>Rango de Fechas (Entrega) <span className="text-danger">*</span></Label>
                      <Flatpickr
                        className="form-control"
                        value={rangoFechas}
                        onChange={(dates) => setRangoFechas(dates.map(d => {
                          // Formatear fecha en zona horaria local para evitar problemas con UTC
                          const year = d.getFullYear();
                          const month = String(d.getMonth() + 1).padStart(2, '0');
                          const day = String(d.getDate()).padStart(2, '0');
                          return `${year}-${month}-${day}`;
                        }))}
                        options={{
                          mode: "range",
                          dateFormat: "Y-m-d",
                          altInput: true,
                          altFormat: "d/m/Y",
                          locale: "es",
                        }}
                        placeholder="Selecciona rango de fechas"
                      />
                    </FormGroup>
                  </Col>
                  <Col md={12} className="d-flex align-items-end gap-2">
                    <Button color="primary" onClick={cargarReporte} disabled={loading}>
                      {loading ? <><Spinner size="sm" className="me-1" />Cargando...</> : <><i className="ri-search-line me-1"></i>Generar Reporte</>}
                    </Button>
                    <Button color="secondary" onClick={limpiarFiltros}>
                      <i className="ri-refresh-line me-1"></i>Limpiar
                    </Button>
                  </Col>
                </Row>

                {/* Información de la Planta Seleccionada */}
                {filtros.planta_id && solicitudes.length > 0 && solicitudes[0].planta_detalle && (
                  <Card className="mb-4 border">
                    <CardBody>
                      <h5 className="card-title mb-3">
                        <i className="ri-building-line me-2"></i>
                        {solicitudes[0].planta_detalle.nombre}
                      </h5>
                      <Row>
                        <Col md={6}>
                          <div className="mb-3">
                            <strong className="text-muted d-block mb-2">Contactos:</strong>
                            {solicitudes[0].planta_detalle.contactos && solicitudes[0].planta_detalle.contactos.length > 0 ? (
                              solicitudes[0].planta_detalle.contactos.map((contacto, idx) => (
                                <div key={idx} className="mb-1">
                                  <i className="ri-user-line me-1"></i>
                                  <strong>{contacto.nombre}</strong>
                                  <br />
                                  <i className="ri-phone-line me-1 ms-3"></i>
                                  <a href={`tel:${contacto.telefono}`}>{contacto.telefono}</a>
                                </div>
                              ))
                            ) : (
                              <span className="text-muted">Sin contactos registrados</span>
                            )}
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="mb-3">
                            <strong className="text-muted d-block mb-2">Dirección:</strong>
                            <p className="mb-2">
                              <i className="ri-map-pin-line me-1"></i>
                              {solicitudes[0].planta_detalle.direccion || "Sin dirección"}
                            </p>
                            {solicitudes[0].planta_detalle.coordenadas && solicitudes[0].planta_detalle.coordenadas.length === 2 && (
                              <a 
                                href={`https://www.google.com/maps?q=${solicitudes[0].planta_detalle.coordenadas[0]},${solicitudes[0].planta_detalle.coordenadas[1]}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-sm btn-soft-primary"
                              >
                                <i className="ri-map-2-line me-1"></i>
                                Ver en Google Maps
                              </a>
                            )}
                          </div>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>
                )}

                {/* Estadísticas del Reporte */}
                {estadisticas && (
                  <Alert color="info" className="mb-4">
                    <div className="d-flex align-items-start">
                      <i className="ri-information-line me-2 fs-18"></i>
                      <div className="flex-grow-1">
                        <strong>Resultados:</strong> {estadisticas.total_solicitudes} solicitudes encontradas
                      </div>
                    </div>
                  </Alert>
                )}

                {/* Tabla de Resultados */}
                {solicitudes.length > 0 ? (
                  <div className="table-responsive">
                    <Table className="table-hover align-middle">
                      <thead className="table-light">
                        <tr>
                          <th>Solicitud</th>
                          <th>Titular</th>
                          <th>Solicitante</th>
                          <th>Delivery</th>
                          <th>Fecha</th>
                        </tr>
                      </thead>
                      <tbody>
                        {solicitudes.map((s, idx) => {
                          // Obtener el último delivery
                          const ultimoDelivery = s.delivery && s.delivery.length > 0 ? s.delivery[s.delivery.length - 1] : null;
                          
                          return (
                            <tr key={idx}>
                              <td>
                                <strong>{s.numero_solicitud || "-"}</strong>
                              </td>
                              <td>
                                <div>
                                  {s.titular.nombre_completo || "-"}
                                  <br />
                                  <small className="text-muted">CI: {s.titular.cedula || "N/A"}</small>
                                </div>
                              </td>
                              <td>
                                <div>
                                  {s.solicitante?.nombre_completo || "-"}
                                  <br />
                                  <small className="text-muted">CI: {s.solicitante?.cedula || "N/A"}</small>
                                </div>
                              </td>
                              <td>
                                {ultimoDelivery && ultimoDelivery.servicioEntrega ? (
                                  <div>
                                    {`${ultimoDelivery.servicioEntrega.nombre || ""} ${ultimoDelivery.servicioEntrega.apellido || ""}`.trim() || "-"}
                                    <br />
                                    <small className="text-muted">
                                      <i className="ri-phone-line me-1"></i>
                                      {ultimoDelivery.servicioEntrega.telefono || "N/A"}
                                    </small>
                                  </div>
                                ) : (
                                  <span className="text-muted">Sin delivery</span>
                                )}
                              </td>
                              <td>
                                {ultimoDelivery?.fecha ? (
                                  <small>{new Date(ultimoDelivery.fecha).toLocaleDateString('es-VE')}</small>
                                ) : (
                                  <span className="text-muted">-</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>
                ) : !loading && (
                  <div className="text-center py-5">
                    <i className="ri-file-search-line fs-1 text-muted"></i>
                    <p className="mt-2 text-muted">
                      {estadisticas ? "No se encontraron solicitudes con los filtros aplicados" : "Selecciona los filtros y haz clic en Generar Reporte"}
                    </p>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default ReportePlantas;
